import { useAssetLogoManifest } from "~/v2/hooks/useAssetLogoManifest";
import { useLocalStorage } from "~/v2/hooks/useLocalStorage";

const normalize = (symbol: string) => symbol.replace(/\//g, "-");

const IconRow = ({
  symbol,
  src,
  dark,
}: {
  symbol: string;
  src: string;
  dark?: boolean;
}) => (
  <div className="flex items-center gap-2 py-1">
    <img src={src} alt={symbol} className="h-6 w-6 shrink-0" loading="lazy" />
    <span className="font-mono text-sm">{symbol}</span>
    {dark && (
      <span className="rounded bg-gray-800 px-1.5 py-0.5 font-semibold text-[10px] text-white leading-none">
        Dark
      </span>
    )}
  </div>
);

export const IconTesting = () => {
  const [open, setOpen] = useLocalStorage<boolean>(
    "devtools-icon-testing-open",
  );
  const { availableSymbols, symbolsWithDark, isLoading } =
    useAssetLogoManifest();

  const baseUrl = window.env?.ASSET_LOGO_BASE_URL;
  const symbols = availableSymbols ? Array.from(availableSymbols).sort() : [];
  const darkSymbols = symbolsWithDark ? Array.from(symbolsWithDark).sort() : [];

  const isOpen = open ?? true;

  return (
    <div className="overflow-hidden rounded border border-gray-300">
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100"
      >
        <h2 className="m-0 font-semibold text-lg">
          Icon Manifest{" "}
          {!isLoading && (
            <span className="font-normal text-gray-500 text-sm">
              ({symbols.length} symbols, {darkSymbols.length} dark)
            </span>
          )}
        </h2>
        <span className="text-gray-500">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && (
        <div className="p-4">
          {isLoading && (
            <p className="text-gray-500 text-sm">Loading manifest…</p>
          )}

          {!isLoading && !baseUrl && (
            <p className="text-red-500 text-sm">
              ASSET_LOGO_BASE_URL is not set.
            </p>
          )}

          {!isLoading && baseUrl && symbols.length === 0 && (
            <p className="text-gray-500 text-sm">
              Manifest loaded but contains no symbols.
            </p>
          )}

          {!isLoading && baseUrl && symbols.length > 0 && (
            <div className="flex gap-12">
              <div className="flex flex-col gap-0.5">
                <h3 className="mb-1 font-semibold text-sm">Light</h3>
                {symbols.map((s) => (
                  <IconRow
                    key={s}
                    symbol={s}
                    src={`${baseUrl}/${normalize(s)}.svg`}
                  />
                ))}
              </div>

              <div className="flex flex-col gap-0.5">
                <h3 className="mb-1 font-semibold text-sm">Dark</h3>
                {darkSymbols.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">None</p>
                ) : (
                  darkSymbols.map((s: string) => (
                    <IconRow
                      key={s}
                      symbol={s}
                      src={`${baseUrl}/dark/${normalize(s)}.svg`}
                      dark
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
